import {
  Autocomplete,
  Box,
  Button,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import Add from '@mui/icons-material/Add';
import DeleteOutline from '@mui/icons-material/DeleteOutlineOutlined';
import type { RecipeLine } from './recipe';
import { skuPicker } from './ui';

/**
 * A titled list of items with a figure each, as a product keeps them: what it is made of
 * (生产用料) and what goes out with it (销售配套). Rows are added on demand.
 */
export function MaterialRows(p: {
  /** Left out inside a card that already names the list. */
  title?: string;
  hint?: string;
  lines: RecipeLine[];
  /** Items to pick from; those used elsewhere on the product are left out by the caller. */
  options: any[];
  max: number;
  disabled: boolean;
  placeholder: string;
  addLabel: string;
  onChange: (lines: RecipeLine[]) => void;
  /** Width of the figure beside each item; narrower in a side panel. */
  figureWidth?: number;
  /** The figure for one picked row, beside the item. */
  figure: (line: RecipeLine, sku: any, set: (ratio: string) => void) => React.ReactNode;
}) {
  const skuOf = (id: string) => p.options.find((s) => s.id === id);
  const set = (i: number, patch: Partial<RecipeLine>) =>
    p.onChange(p.lines.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  return (
    <Box>
      {p.title && <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{p.title}</Typography>}
      {p.hint && (
        <Typography color="text.secondary" sx={{ fontSize: 12, mt: 0.25, mb: 1.25 }}>
          {p.hint}
        </Typography>
      )}
      <Stack spacing={1}>
        {p.lines.map((line, i) => {
          const sku = skuOf(line.skuId);
          return (
            <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Autocomplete<any>
                size="small"
                sx={{ flex: 1, minWidth: 0 }}
                options={p.options.filter(
                  (s) => s.id === line.skuId || !p.lines.some((l) => l.skuId === s.id),
                )}
                value={sku ?? null}
                disabled={p.disabled}
                {...skuPicker()}
                onChange={(_, v) => set(i, { skuId: v?.id ?? '', ratio: '' })}
                renderInput={(params) => <TextField {...params} placeholder={p.placeholder} />}
              />
              <Box sx={{ width: p.figureWidth ?? 168, flexShrink: 0 }}>
                {sku && p.figure(line, sku, (ratio) => set(i, { ratio }))}
              </Box>
              <Tooltip title="删除这行">
                <span>
                  <IconButton
                    size="small"
                    disabled={p.disabled}
                    onClick={() => p.onChange(p.lines.filter((_, j) => j !== i))}
                  >
                    <DeleteOutline fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          );
        })}
      </Stack>
      <Button
        size="small"
        startIcon={<Add />}
        disabled={p.disabled || p.lines.length >= p.max}
        sx={{ mt: p.lines.length ? 1 : 0, ml: -0.5 }}
        onClick={() => p.onChange([...p.lines, { skuId: '', ratio: '' }])}
      >
        {p.addLabel}
      </Button>
    </Box>
  );
}
